import type { ComponentMultiStyleConfig } from '@chakra-ui/theme';
import { Radii, Colors, FontSizes, Space, FontWeights, Shadows } from '../../types';

const sizeMd = (props: any) => {
  const { orientation } = props;
  const isVertical = orientation === 'vertical';
  return {
    tab: {
      py: isVertical ? Space.XS : Space.SM,
      px: isVertical ? Space.MD : Space.SM,
    },
  };
};

const baseStyle = (props: any) => {
  const { orientation, isFitted } = props;
  const isVertical = orientation === 'vertical';
  const alignProp = isVertical ? 'justifyContent' : 'alignItems';
  const alignSetting = isVertical ? 'flex-start' : 'center';
  const maxWidthSetting = isFitted ? 'unset' : '200px';
  return {
    tablist: {
      overflowX: isVertical || isFitted ? undefined : 'auto',
      boxSizing: 'border-box',
      scrollbarWidth: 'thin',
      overflowY: 'visible',
      isolation: 'isolate',
    },
    tab: {
      fontSize: FontSizes.MD,
      fontWeight: FontWeights.MEDIUM,
      lineHeight: 'shorter',
      maxWidth: { base: maxWidthSetting, md: 'initial' },
      textWrap: 'balance',
      gap: Space.XS,
      [alignProp]: alignSetting,
      whiteSpace: 'normal',
      hyphens: 'auto',
      textAlign: 'start',
      position: 'relative',
      '.svg-inline--fa': {
        alignSelf: 'baseline',
        color: Colors.ICON_DEFAULT,
      },
      _hover: {
        bg: Colors.SURFACE_HOVER,
      },
      _selected: {
        color: Colors.TEXT_EMPHASIS,
      },
    },
  };
};

const lineStyle = (props: any) => {
  const { orientation } = props;
  const isVertical = orientation === 'vertical';
  const borderProp = isVertical ? 'borderEndWidth' : 'borderBottomWidth';
  return {
    tablist: {
      m: 0,
      borderWidth: 0,
      boxShadow: isVertical
        ? 'inset -1px 0 0 0 var(--udl-colors-border-subdued)'
        : 'inset 0 -1px 0 0 var(--udl-colors-border-subdued)',
    },
    tab: {
      m: 0,
      borderWidth: 0,
      [borderProp]: '3px',
      borderColor: Colors.TRANSPARENT,
      _active: {
        bg: 'unset',
      },
      _selected: {
        '--tabs-color': `colors.${Colors.SURFACE_PRIMARY}`,
        borderColor: 'var(--tabs-color)',
        color: Colors.TEXT_EMPHASIS,
        _dark: {
          '--tabs-color': `colors.${Colors.SURFACE_PRIMARY}`,
          color: 'var(--tabs-color)',
        },
      },
    },
  };
};

const enclosedStyle = (props: any) => {
  const { orientation } = props;
  const isVertical = orientation === 'vertical';
  const roundedEndProp = isVertical ? 'roundedRight' : 'roundedBottom';
  const borderEndColorProp = isVertical ? 'borderRightColor' : 'borderBottomColor';

  return {
    tablist: {
      m: 0,
      borderWidth: 0,
      boxShadow: isVertical
        ? 'inset -1px 0 0 0 var(--udl-colors-border-default)'
        : 'inset 0 -1px 0 0 var(--udl-colors-border-default)',
    },
    tab: {
      borderWidth: '1px',
      borderRadius: Radii.LG,
      [roundedEndProp]: 0,
      hover: {
        bg: Colors.SURFACE_HOVER,
      },
      mb: 0,
      _selected: {
        borderColor: Colors.BORDER_DEFAULT,
        [borderEndColorProp]: Colors.SURFACE_PAGE,
        bg: Colors.SURFACE_PAGE,
        color: Colors.TEXT_EMPHASIS,
      },
      '[aria-orientation="vertical"] &': {
        me: 0,
        [roundedEndProp]: 0,
        textAlign: 'center',
        justifyContent: 'center',
        _selected: {
          borderColor: Colors.BORDER_DEFAULT,
          borderRightColor: Colors.TRANSPARENT,
        },
      },
    },
  };
};

const segmentedStyle = (props: any) => {
  const { isFitted } = props;
  return {
    tab: {
      rounded: Radii.LG,
      hover: {
        bg: Colors.SURFACE_HOVER,
      },
      _selected: {
        bg: Colors.SURFACE_ELEVATED,
        shadow: Shadows.XS,
      },
    },
    tablist: {
      bg: Colors.SURFACE_NEUTRAL_SUBDUED,
      rounded: 'calc(var(--udl-radii-lg) + 2px)',
      p: '0.5',
      w: isFitted ? 'full' : 'fit-content',
    },
  };
};

const pillStyle = () => {
  return {
    tablist: {
      gap: Space['2XS'],
    },
    tab: {
      py: Space.XS,
      px: Space.SM,
      rounded: Radii.LG,
      _selected: {
        bg: Colors.SURFACE_ACTIVE,
      },
    },
  };
};

export const Tabs: ComponentMultiStyleConfig = {
  baseStyle,
  variants: {
    line: lineStyle,
    pill: pillStyle,
    segmented: segmentedStyle,
    enclosed: enclosedStyle,
  },
  parts: [],
  sizes: {
    md: sizeMd,
  },
};
